import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import Price from '../components/Price';
import sumInCurrency from '../utils/sumInCurrency';
import { formatMoney } from '../utils/fx';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const STATUS_LABEL = {
  pending: 'Pendiente',
  paid: 'Pagada',
  shipped: 'Enviada',
  delivered: 'Entregada',
  cancelled: 'Cancelada',
};

export default function Seller() {
  const { user, loading, listMyInvites, generateInvite, listMySales, updateOrderStatus,
    startConnectOnboarding, getConnectStatus, openConnectDashboard } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Cobros con tarjeta (Stripe Connect): estado de la cuenta del vendedor y
  // comisión vigente de Troly (pública) para mostrarle cuánto recibe.
  const [connect, setConnect] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);
  const [commission, setCommission] = useState(0);
  const { displayCurrency, rates } = useCurrency();
  const { i18n } = useTranslation();
  const [invites, setInvites] = useState([]);
  const [sales, setSales] = useState({ orders: [], forSale: [], metrics: { totalOrders: 0, totalRevenue: 0, totalUnits: 0, byStatus: {}, inventory: { activePublications: 0, availableUnits: 0, soldOut: 0 } } });
  const [tab, setTab] = useState('forSale');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [invEmail, setInvEmail] = useState('');
  const [invFlash, setInvFlash] = useState('');

  const refresh = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const [m, s] = await Promise.all([listMyInvites(), listMySales()]);
      setInvites(m.invites || []);
      setSales(s);
    } catch (e) {
      setErr(e.response?.data?.message || 'Error cargando datos');
    } finally { setBusy(false); }
  }, [listMyInvites, listMySales]);

  // Panel habilitado para sellers aprobados (y admin).
  const canSell = !!user && (user.role === 'seller' || user.role === 'admin' || user.sellerStatus === 'approved');

  useEffect(() => {
    if (canSell) refresh();
  }, [canSell, refresh]);

  // Estado de Connect. Al volver del onboarding (?connect=return) esto
  // consulta a Stripe y sincroniza; con ?connect=refresh el link venció o ya
  // se usó, así que se pide uno nuevo y se vuelve al onboarding.
  useEffect(() => {
    if (!canSell) return;
    const flag = searchParams.get('connect');
    if (flag) setSearchParams({}, { replace: true });
    if (flag === 'refresh') {
      startConnectOnboarding()
        .then(({ url }) => { window.location.href = url; })
        .catch((e) => setErr(e.response?.data?.message || 'No se pudo reanudar la activación de cobros'));
      return;
    }
    getConnectStatus().then(setConnect).catch(() => setConnect({ connect: null, configured: false }));
    axios.get(`${API}/api/settings/public`)
      .then(({ data }) => setCommission(data.marketplaceCommissionEnabled ? (data.marketplaceCommissionPct || 0) : 0))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSell]);

  const goConnect = async (fn) => {
    setConnectBusy(true); setErr('');
    try {
      const { url } = await fn();
      window.location.href = url;
    } catch (e) {
      setErr(e.response?.data?.message || 'No se pudo abrir Stripe');
      setConnectBusy(false);
    }
  };

  const handleInvite = async () => {
    setInvFlash('');
    try {
      const { code, emailSent } = await generateInvite(invEmail.trim() || undefined);
      setInvFlash(emailSent ? `Código ${code} enviado a ${invEmail}` : `Código generado: ${code.slice(0,3)}-${code.slice(3)}`);
      setInvEmail('');
      refresh();
    } catch (e) {
      setInvFlash(e.response?.data?.message || 'Error generando invitación');
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await updateOrderStatus(id, status);
      refresh();
    } catch (e) {
      setErr(e.response?.data?.message || 'Error actualizando orden');
    }
  };

  if (loading) return <div className="profile"><div className="profile-body container"><p>Cargando…</p></div></div>;
  if (!user) return <Navigate to="/trigger" replace />;
  if (!canSell) return <Navigate to="/profile" replace />;

  const { metrics } = sales;
  const forSale = sales.forSale || [];
  const used = invites.filter((i) => i.usedBy).length;
  const inventory = metrics.inventory || { activePublications: 0, availableUnits: 0, soldOut: 0 };
  const pendingAction = (metrics.byStatus?.pending || 0) + (metrics.byStatus?.paid || 0);
  const toShip = metrics.byStatus?.paid || 0;

  // Total convertido a la divisa display, reutilizado para card de ventas y ticket promedio.
  const nonCancelled = (sales.orders || []).filter((o) => o.status !== 'cancelled');
  const totalConv = sumInCurrency(
    nonCancelled.map((o) => ({ amount: Number(o.total) || 0, currency: o.currency || 'USD' })),
    displayCurrency,
    rates,
  );
  const ticketAvg = nonCancelled.length > 0 ? totalConv / nonCancelled.length : 0;

  return (
    <div className="profile">
      <Helmet><title>Panel de Vendedor — Troly</title></Helmet>
      <div className="profile-body container" style={{ maxWidth: 1100 }}>
        <header className="profile-header fade-up" style={{ alignItems: 'flex-start', textAlign: 'left' }}>
          <div>
            <h1 style={{ fontSize: 28, marginBottom: 4 }}>Panel de Vendedor</h1>
            <p style={{ color: 'var(--muted)', margin: 0 }}>
              Hola {user.username || user.name} · {invites.length}/{(user.invitesLeft || 0) + invites.length} invitaciones generadas · {user.invitesLeft} restantes
            </p>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Link to="/profile" className="btn-ghost">Mi perfil público</Link>
            <Link to="/upload?action=Venta" className="btn-teal">+ Subir pieza en venta</Link>
          </div>
        </header>

        {err && <div className="error-banner" style={{ marginTop: 14 }}>{err}</div>}

        {/* ── Cobros con tarjeta (Stripe Connect) ── */}
        {connect && connect.configured !== false && (() => {
          const c = connect.connect || {};
          const ready = c.chargesEnabled;
          const started = !!c.accountId;
          const pending = (connect.requirements || []).length;
          return (
            <div className="admin-card admin-switch-row fade-up" style={{ marginTop: 16 }}>
              <div className="admin-switch-row__text">
                <span className="admin-switch-row__title">
                  Cobros con tarjeta{' '}
                  <span className={`pill pill--${ready ? 'done' : started ? 'wait' : 'err'}`}>
                    {ready ? 'Activos' : started ? 'Pendiente' : 'Sin activar'}
                  </span>
                </span>
                <span className="admin-switch-row__hint">
                  {ready
                    ? `Tus compradores pagan con tarjeta en Troly y el dinero se deposita en tu cuenta bancaria vía Stripe.${commission > 0 ? ` Troly retiene ${commission}% por venta.` : ''}${!c.payoutsEnabled ? ' Stripe aún no libera tus depósitos: revisa tu cuenta.' : ''}`
                    : started
                      ? `Te faltan datos en Stripe${pending ? ` (${pending} pendientes)` : ''}. Mientras tanto tus ventas se coordinan por mensaje, como hasta ahora.`
                      : 'Activa los cobros para que te paguen con tarjeta directo en Troly. Stripe te pide identificación, CLABE y RFC (toma unos 5 minutos).'}
                </span>
              </div>
              <button
                type="button"
                className={ready ? 'btn-ghost' : 'btn-teal'}
                disabled={connectBusy}
                onClick={() => goConnect(ready ? openConnectDashboard : startConnectOnboarding)}
              >
                {connectBusy ? 'Abriendo Stripe…' : ready ? 'Ver pagos y depósitos' : started ? 'Continuar activación' : 'Activar cobros'}
              </button>
            </div>
          );
        })()}

        <div className="stats-grid fade-up" style={{ marginTop: 16, marginBottom: 0 }}>
          <div className="stat-card">
            <div className="stat-card__top"><span className="stat-card__label">VENTAS TOTALES</span></div>
            <div className="stat-card__val">{formatMoney(totalConv, displayCurrency, i18n.language)}</div>
            <span className="stat-sub">acumulado</span>
          </div>
          <div className="stat-card">
            <div className="stat-card__top"><span className="stat-card__label">ÓRDENES</span></div>
            <div className="stat-card__val">{metrics.totalOrders}</div>
            <span className="stat-sub">{metrics.byStatus?.pending || 0} pendientes</span>
          </div>
          <div className="stat-card">
            <div className="stat-card__top"><span className="stat-card__label">UNIDADES VENDIDAS</span></div>
            <div className="stat-card__val">{metrics.totalUnits}</div>
            <span className="stat-sub">unidades despachadas o por despachar</span>
          </div>
          <div className="stat-card">
            <div className="stat-card__top"><span className="stat-card__label">INVITACIONES</span></div>
            <div className="stat-card__val">{user.invitesLeft}</div>
            <span className="stat-sub">{used} usadas · {invites.length - used} en circulación</span>
          </div>
        </div>

        <div className="stats-grid stats-grid--three fade-up" style={{ marginTop: 12 }}>
          <div className="stat-card">
            <div className="stat-card__top"><span className="stat-card__label">ACCIONES PENDIENTES</span></div>
            <div className="stat-card__val">{pendingAction}</div>
            <span className="stat-sub">{toShip} por enviar · {metrics.byStatus?.pending || 0} por confirmar</span>
          </div>
          <div className="stat-card">
            <div className="stat-card__top"><span className="stat-card__label">INVENTARIO ACTIVO</span></div>
            <div className="stat-card__val">{inventory.availableUnits}</div>
            <span className="stat-sub">
              {inventory.activePublications} {inventory.activePublications === 1 ? 'pieza en venta' : 'piezas en venta'}
              {inventory.soldOut > 0 ? ` · ${inventory.soldOut} agotadas` : ''}
            </span>
          </div>
          <div className="stat-card">
            <div className="stat-card__top"><span className="stat-card__label">TICKET PROMEDIO</span></div>
            <div className="stat-card__val">{formatMoney(ticketAvg, displayCurrency, i18n.language)}</div>
            <span className="stat-sub">por orden ({nonCancelled.length})</span>
          </div>
        </div>

        <div className="admin-tabs" style={{ marginTop: 24 }}>
          <button className={`admin-tab${tab==='forSale'?' admin-tab--active':''}`} onClick={() => setTab('forSale')}>Piezas en venta ({forSale.length})</button>
          <button className={`admin-tab${tab==='metrics'?' admin-tab--active':''}`} onClick={() => setTab('metrics')}>Ventas ({sales.orders.length})</button>
          <button className={`admin-tab${tab==='invites'?' admin-tab--active':''}`} onClick={() => setTab('invites')}>Invitaciones ({invites.length})</button>
          <button className="btn-ghost" style={{ marginLeft: 'auto' }} onClick={refresh} disabled={busy}>{busy ? 'Actualizando…' : 'Actualizar'}</button>
        </div>

        {tab === 'forSale' && (
          forSale.length === 0 ? (
            <div className="admin-empty">
              Aún no tienes piezas en venta. Al marcar una pieza como <strong>Venta</strong> aparece aquí y entra a tu carpeta «Ventas».
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}></th>
                    <th>Nombre</th>
                    <th style={{ width: 150 }}>SKU</th>
                    <th style={{ width: 120 }}>Cantidad</th>
                    <th style={{ width: 120 }}>Precio</th>
                    <th style={{ width: 90 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {forSale.map((p) => {
                    const total = p.units || 0;
                    const left = Math.max(0, total - (p.unitsSold || 0));
                    const sku = p.sku || p.toyNumber;
                    return (
                      <tr key={p._id}>
                        <td>
                          {p.thumbnails?.[0]
                            ? <img src={p.thumbnails[0]} alt="" loading="lazy" width={50} height={50} style={{ objectFit: 'cover', borderRadius: 6 }} />
                            : <div style={{ width: 50, height: 50, background: 'var(--bg)', borderRadius: 6 }} />}
                        </td>
                        <td>
                          <Link to={`/listing/${p.listing || p._id}`} style={{ fontWeight: 600, color: 'inherit', textDecoration: 'none' }}>{p.name}</Link>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{[p.brand, p.scale].filter(Boolean).join(' · ')}</div>
                        </td>
                        <td style={{ fontFamily: 'var(--troly-font-mono)', fontSize: 12 }}>
                          {sku || <span style={{ color: 'var(--muted)' }}>Sin SKU</span>}
                        </td>
                        <td>
                          {left === 0
                            ? <span className="pill pill--err">Agotada</span>
                            : <span style={{ fontFamily: 'var(--troly-font-mono)' }}>{left}{total !== left ? <span style={{ color: 'var(--muted)' }}> / {total}</span> : null}</span>}
                        </td>
                        <td>
                          {p.price?.amount > 0
                            ? <Price amount={Number(p.price.amount)} currency={p.price.currency || 'USD'} />
                            : <span style={{ color: 'var(--muted)' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Link to={`/upload/${p._id}`} className="btn-ghost" style={{ fontSize: 12 }}>Editar</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === 'metrics' && (
          sales.orders.length === 0 ? (
            <div className="admin-empty">Aún no tienes ventas. Sube piezas con inventario y ¡a vender!</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}></th>
                  <th>Pieza</th>
                  <th>Comprador</th>
                  <th style={{ width: 80 }}>Unid.</th>
                  <th style={{ width: 110 }}>Total</th>
                  <th style={{ width: 130 }}>Estado</th>
                  <th style={{ width: 220 }}></th>
                </tr>
              </thead>
              <tbody>
                {sales.orders.map((o) => (
                  <tr key={o._id}>
                    <td>
                      {o.publication?.images?.[0]
                        ? <img src={o.publication.images[0]} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6 }} />
                        : <div style={{ width: 50, height: 50, background: 'var(--bg)', borderRadius: 6 }} />}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{o.publication?.name || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{o.shipping?.city}, {o.shipping?.country}</div>
                    </td>
                    <td>{o.buyer?.username || o.buyer?.name || '—'}</td>
                    <td>{o.units}</td>
                    <td><Price amount={Number(o.total)} currency={o.currency || 'USD'} /></td>
                    <td>
                      <span className={`pill pill--${o.status === 'cancelled' ? 'err' : o.status === 'delivered' ? 'done' : 'wait'}`}>{STATUS_LABEL[o.status] || o.status}</span>
                      {o.payment?.method === 'stripe' && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                          {o.payment.status === 'awaiting' ? 'Esperando pago con tarjeta'
                            : o.payment.status === 'refunded' ? 'Reembolsada'
                            : o.payment.status === 'expired' ? 'Pago no concretado'
                            : 'Pagada con tarjeta'}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <select
                        className="light-select"
                        style={{ fontSize: 12 }}
                        value={o.status}
                        onChange={(e) => {
                          if (e.target.value === 'cancelled' && o.payment?.status === 'paid'
                              && !window.confirm('Cancelar esta orden reembolsa el pago completo al comprador. ¿Continuar?')) return;
                          changeStatus(o._id, e.target.value);
                        }}
                      >
                        {Object.entries(STATUS_LABEL).map(([k, v]) => {
                          // En órdenes con tarjeta, "Pagada" y "Pendiente" las pone Stripe.
                          const locked = o.payment?.method === 'stripe' && k !== o.status && (k === 'paid' || k === 'pending');
                          return <option key={k} value={k} disabled={locked}>{v}</option>;
                        })}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {tab === 'invites' && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <input className="light-input" placeholder="Correo del invitado (opcional)" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} style={{ flex: 1 }} />
              <button className="btn-teal" onClick={handleInvite} disabled={user.invitesLeft <= 0}>Generar invitación</button>
            </div>
            {invFlash && <div className="admin-flash admin-flash--ok" style={{ marginTop: 10 }}>{invFlash}</div>}
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>Como vendedor, dispones de hasta 999 invitaciones para ampliar la red Troly.</p>

            {invites.length === 0 ? (
              <div className="admin-empty">Aún no has generado invitaciones.</div>
            ) : (
              <table className="admin-table" style={{ marginTop: 14 }}>
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Código</th>
                    <th>Enviada a</th>
                    <th style={{ width: 130 }}>Creada</th>
                    <th style={{ width: 180 }}>Estado</th>
                    <th>Usada por</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => {
                    const used = !!inv.usedBy;
                    const expired = !used && new Date(inv.expiresAt) < new Date();
                    return (
                      <tr key={inv._id}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--teal)' }}>{inv.code.slice(0,3)}-{inv.code.slice(3)}</td>
                        <td>{inv.sentTo || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                        <td>{new Date(inv.createdAt).toLocaleDateString()}</td>
                        <td><span className={`pill pill--${used ? 'done' : expired ? 'err' : 'wait'}`}>{used ? 'Usada' : expired ? 'Expirada' : inv.emailStatus === 'sent' ? 'Enviada · pendiente' : 'Solo código'}</span></td>
                        <td>{used ? (inv.usedBy.name || inv.usedBy.username) : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
